<template>
  <lf-dropdown
    v-if="showCallText || hasTextData"
    ref="callDispositionSelector"
    :options="callStatuses"
    :value="callDisposition"
    name="call-disposition"
    class="call-disposition-selector w-fit-content cursor-pointer"
    :teleport-options="teleportOptions"
    custom-z-index="z-101"
    :dropdown-offset-top="dropdownOffsetTop"
  >
    <template #toggle-button="{ toggle, value, menuOpen }">
      <lf-pill
        data-cy="disposition-dropdown"
        :color-index="currentColorOfStatus"
        class="w-fit-content"
        size="sm"
        @click.stop="toggle"
      >
        <div class="flex items-center space-x-2">
          <p class="text-xxs font-semibold uppercase">
            {{ value }}
          </p>
          <icon-base
            height="6"
            width="10"
            :icon="IconDropdownArrow"
            :class="{ 'rotate-180 transform': menuOpen }"
          />
        </div>
      </lf-pill>
    </template>
    <template #listItem="{ option, optionKey }">
      <div
        class="flex cursor-pointer items-center space-x-2 rounded p-2 hover:bg-primary-background-active"
        :class="{ 'bg-gray-100': callDisposition === option }"
        @click="handleCallDispositionUpdate(optionKey)"
      >
        <div
          class="h-1-5 w-1-5 rounded-full"
          :style="callDispositionOptionStyle(optionKey)"
        />
        <div class="w-30 text-headline">{{ option }}</div>
        <icon-base
          v-if="callDisposition === option"
          :icon="IconCheck"
          class="text-primary"
        />
      </div>
    </template>
  </lf-dropdown>
  <lf-pill
    v-else
    data-cy="disposition-inactive"
    :color-index="currentColorOfStatus"
    size="sm"
    class="w-fit-content"
  >
    <p class="text-xxs font-semibold uppercase">
      {{ callDisposition }}
    </p>
  </lf-pill>
</template>
<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { COLORS } from "@/helpers/constants";
import { COLORS_BY_DISPOSITION_NOTE_STATUS } from "@/helpers/constants/colors";
import { CALL_UPDATED } from "@/helpers/constants/events";
import { useEmitter } from "@/hooks/common";
import { useUpdateCallNoteMutation } from "@/hooks/queries/useInbox";

import IconCheck from "@/components/icons/IconCheck.vue";
import IconDropdownArrow from "@/components/icons/IconDropdownArrow.vue";
import IconBase from "@/components/ui/IconBase.vue";
import LfDropdown, {
  type DropdownTeleportOptions,
} from "@/components/ui/inputs/LfDropdown.vue";
import LfPill from "@/components/ui/text/LfPill.vue";

import type { CallLogData } from "@/models/communicationLogs";

const {
  showCallText,
  logData,
  teleportOptions,
  dropdownOffsetTop = 0,
} = defineProps<{
  showCallText: boolean;
  logData: CallLogData;
  teleportOptions?: Partial<DropdownTeleportOptions>;
  dropdownOffsetTop?: number;
}>();

const { getters } = useStore();
const { t } = useI18n();
const emitter = useEmitter();
const { mutateAsync: updateCallNoteMutation } = useUpdateCallNoteMutation();

const callDispositionSelector = useTemplateRef("callDispositionSelector");

const callStatuses = computed(() =>
  Object.entries(callDispositionOptions.value).reduce(
    (acc: Record<string, string>, [id, value]) => {
      acc[id] = value;
      return acc;
    },
    {}
  )
);

const callDispositionOptions = computed<string[]>(
  () => getters["options/callDispositions"]
);

const currentColorOfStatus = computed(() => {
  return COLORS_BY_DISPOSITION_NOTE_STATUS[
    logData.call_disposition as keyof typeof COLORS_BY_DISPOSITION_NOTE_STATUS
  ];
});

const hasTextData = computed(() => {
  const { body, has_summary, has_transcription } = logData;

  return !!body || !!has_summary || !!has_transcription;
});

const callDisposition = computed(
  () =>
    logData.call_disposition?.replace(/_/g, " ") ??
    t("ACTIVITY.SELECT_CALL_DISPOSITION")
);
const handleCallDispositionUpdate = async (option: string) => {
  const payload = {
    callSessionId: logData.id,
    note: logData.body ?? "",
    call_disposition: option,
  };

  callDispositionSelector.value?.toggleMenu();

  await updateCallNoteMutation(payload);
  emitter.emit(CALL_UPDATED);
};

const callDispositionOptionStyle = (optionKey: string) => {
  const key = optionKey as keyof typeof COLORS_BY_DISPOSITION_NOTE_STATUS;
  const color = COLORS[COLORS_BY_DISPOSITION_NOTE_STATUS[key]];
  return `background-color:rgb(${color})`;
};
</script>
